import { Component, DestroyRef, OnInit } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { AuthService } from '../../../core/services/auth.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { Menu } from 'primeng/menu';
import { AsyncPipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UserDto } from '../../../core/api/_dtos/user.dto';
import { AppButton } from '../../../features/client/components/atom/app-button/app-button';
import { I18nService } from '../../services/i18n.service';
import { AvatarInitialsComponent } from '../avatar-initials/avatar-initials.component';
import { SUPPORT_EMAIL } from '../../../app.constants';

@Component({
  selector: 'app-user-avatar',
  imports: [
    Menu,
    AsyncPipe,
    TranslatePipe,
    AppButton,
    AvatarInitialsComponent,
  ],
  templateUrl: './user-avatar.html',
  styleUrl: './user-avatar.scss',
  standalone: true
})
export class UserAvatarComponent implements OnInit {
  userMenuItems: MenuItem[] = [];
  private currentUser: UserDto | null = null;

  constructor(
    protected auth: AuthService,
    private translate: TranslateService,
    private router: Router,
    private destroyRef: DestroyRef,
    private i18n: I18nService
  ) {
  }

  ngOnInit(): void {
    // Rebuild menu on user change
    this.auth.currentUser$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(user => {
        this.currentUser = user;
        this.buildUserMenuItems(user);
      });

    // Rebuild menu on language change
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.buildUserMenuItems(this.currentUser);
      });
  }

  private buildUserMenuItems(user: UserDto | null): void {
    const items: MenuItem[] = [
      // User info header
      {
        label: user?.full_name || '',
        icon: 'pi pi-user',
        disabled: true,
        styleClass: 'user-menu-header'
      },
      {
        label: user?.email || '',
        disabled: true,
        styleClass: 'user-menu-subheader'
      },
      { separator: true },
      // Account menu items
      {
        label: this.translate.instant('app.user_menu.my_account'),
        icon: 'pi pi-cog',
        command: () => this.goToAccountSettings()
      },
      {
        label: this.translate.instant('app.user_menu.help'),
        icon: 'pi pi-question-circle',
        command: () => this.openSupport()
      }
    ];

    // Add admin option if user is admin
    if (user && this.isUserAdmin(user)) {
      items.push({ separator: true });
      items.push({
        label: this.translate.instant('app.user_menu.admin'),
        icon: 'pi pi-shield',
        command: () => this.goToAdmin()
      });
    }

    // Logout
    items.push({ separator: true });
    items.push({
      label: this.translate.instant('app.user_menu.logout'),
      icon: 'pi pi-sign-out',
      command: () => this.auth.logout()
    });

    this.userMenuItems = items;
  }

  private isUserAdmin(user: UserDto): boolean {
    // Check if user has admin role
    return user.roles?.includes('admin') ?? false;
  }

  private goToAccountSettings(): void {
    const url = this.i18n.generateUrlWithLang('client/orders');
    this.router.navigateByUrl(url);
  }

  private openSupport(): void {
    window.location.href = `mailto:${SUPPORT_EMAIL}`;
  }

  private goToAdmin(): void {
    const url = this.i18n.generateUrlWithLang('admin/dashboard');
    this.router.navigateByUrl(url);
  }

  onLoginClick(): void {
    this.auth.handleLoginDialog(undefined, undefined, { origin: 'voluntary' }).subscribe();
  }
}

